import { useEffect, useRef, useState } from "react";
import { AppDetails } from "src/types";

export const useAppDetailsBySlugs = ({
  slugs,
  skip = false,
}: {
  slugs: string[];
  skip?: boolean;
}) => {
  const [appDetailsMap, setAppDetailsMap] = useState<Map<string, AppDetails>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const prevSlugsRef = useRef<string[]>([]);

  useEffect(() => {
    if (skip) return;

    const areArraysEqual = (arr1: string[], arr2: string[]) => {
      if (arr1.length !== arr2.length) return false;

      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
      }

      return true;
    };

    if (areArraysEqual(prevSlugsRef.current, slugs)) {
      return; // Nothing to fetch
    }

    prevSlugsRef.current = slugs;

    const fetchAppDetails = async (slug: string) => {
      const response = await fetch(`/api/v4/apps/${encodeURIComponent(slug)}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch app details for "${slug}"`);
      }

      return response.json();
    };

    const fetchAllAppDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const uniqueSlugs = Array.from(new Set(slugs));

        const appDetailsArray = await Promise.allSettled(
          uniqueSlugs.map(async (slug) => {
            const { image, name } = await fetchAppDetails(slug);
            return [slug, { image, name }] as const;
          }),
        );

        setAppDetailsMap(
          new Map(
            appDetailsArray
              .filter((result) => result.status === "fulfilled")
              .map((result) => result.value),
          ),
        );
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllAppDetails();
  }, [slugs, skip]);

  return { data: appDetailsMap, isLoading, error };
};
